import { useEffect, useRef } from "react";
import { BrowserRouter, useLocation } from "react-router";
import { useAuth } from "react-oidc-context";
import { createLogger } from "./utils/logger";
import ApplicationRoutes from "./routes/applicationRoutes";
import "./styles/app.css";
import { useAppDispatch } from "./redux/hooks";
import { setAuthSyncComplete } from "./redux/user/userSlice";
import {
  ACCESS_TOKEN_STORAGE_KEY,
  REFRESH_TOKEN_EXPIRY_STORAGE_KEY,
  REFRESH_TOKEN_STORAGE_KEY,
  TOKEN_REFRESH_LEAD_TIME,
  getEarliestTokenExpiry,
  getExpiryFromToken,
  verifyToken,
} from "./config/getToken";
import { runtimeConfig } from "./config/runtimeConfig";

const logger = createLogger("App");

const IDLE_TIMEOUT = runtimeConfig.idleTimeoutMs;

const AppContent = () => {
  const location = useLocation();
  // Just for QA/DEV/localhost testing: keep URL id in session storage.
  useEffect(() => {
    const hostname = window.location.hostname;

    if (
      hostname.startsWith("qa") ||
      hostname.startsWith("dev") ||
      hostname.startsWith("localhost")
    ) {
      const params = new URLSearchParams(location.search);
      const queryId = params.get("id");

      if (queryId) {
        sessionStorage.setItem("queryId", queryId);
      } else {
        sessionStorage.removeItem("queryId");
      }
    }

    return () => {
      sessionStorage.removeItem("queryId");
    };
  }, [location.search]);
  return <ApplicationRoutes />;
};

const App = () => {
  const auth = useAuth();
  const dispatch = useAppDispatch();
  const idleTimer = useRef<number | null>(null);
  const isLoggingOut = useRef(false);

  useEffect(() => {
    try {
      sessionStorage.removeItem("UUID");
      sessionStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
      sessionStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
      sessionStorage.removeItem(REFRESH_TOKEN_EXPIRY_STORAGE_KEY);
      sessionStorage.removeItem("notificationFilters");
    } catch (error) {
      logger.warn("Failed to refresh session storage:", error);
    }
  }, []);

  useEffect(() => {
    if (auth.error) {
      dispatch(setAuthSyncComplete());
      return;
    }

    if (!auth.isLoading && auth.isAuthenticated) {
      const accessToken = auth.user?.access_token;
      const refreshToken = auth.user?.refresh_token;
      if (refreshToken && accessToken) {
        try {
          sessionStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshToken);
          sessionStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, accessToken);
        } catch (error) {
          logger.warn(
            "Failed to persist refresh token in session storage:",
            error,
          );
        }
      }

      const expiry = getExpiryFromToken(refreshToken);
      if (expiry !== undefined) {
        try {
          sessionStorage.setItem(
            REFRESH_TOKEN_EXPIRY_STORAGE_KEY,
            String(expiry),
          );
        } catch (error) {
          logger.warn(
            "Failed to persist refresh token expiry in session storage:",
            error,
          );
        }
      }

      const linkUuid = auth.user?.profile?.sub;
      if (linkUuid) {
        try {
          sessionStorage.setItem("UUID", linkUuid);
        } catch (error) {
          logger.warn("Failed to persist UUID in session storage:", error);
        }
      }
      dispatch(setAuthSyncComplete());
    }
  }, [auth.error, auth.isLoading, auth.isAuthenticated, auth.user, dispatch]);

  useEffect(() => {
    if (!auth.isAuthenticated || auth.isLoading) {
      return;
    }

    let timer: number | undefined;
    let isActive = true;

    const scheduleVerification = () => {
      const accessToken = sessionStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
      const expiry = getEarliestTokenExpiry(accessToken);
      if (expiry === undefined) {
        logger.warn(
          "Cannot schedule token verification: access token has no valid expiry",
        );
        return;
      }
      const delay = Math.max(
        expiry * 1000 - Date.now() - TOKEN_REFRESH_LEAD_TIME,
        0,
      );

      logger.info(
        "Scheduled token verification",
        JSON.stringify({
          delayMinutes: Math.floor(delay / 60000),
        }),
      );

      timer = window.setTimeout(async () => {
        logger.info("Running scheduled token verification");
        try {
          await verifyToken();
        } catch (error) {
          logger.warn(
            "Scheduled token verification failed:",
            JSON.stringify(error),
          );
          isActive = false;
          sessionStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
          sessionStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
          sessionStorage.removeItem(REFRESH_TOKEN_EXPIRY_STORAGE_KEY);

          if (!isLoggingOut.current) {
            isLoggingOut.current = true;
            auth.signoutRedirect().catch((signoutError) => {
              logger.error(
                "Signout after token refresh failure failed:",
                signoutError,
              );
              isLoggingOut.current = false;
            });
          }
        } finally {
          if (isActive) {
            scheduleVerification();
          }
        }
      }, delay);
    };

    scheduleVerification();

    return () => {
      isActive = false;
      if (timer !== undefined) {
        window.clearTimeout(timer);
      }
    };
  }, [auth.isAuthenticated, auth.isLoading, auth.signoutRedirect]);

  useEffect(() => {
    // Only set up idle timer if user is authenticated
    if (!auth.isAuthenticated || auth.isLoading) {
      return;
    }

    const resetTimer = () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => {
        // Prevent multiple simultaneous logout attempts
        if (!isLoggingOut.current && auth.isAuthenticated) {
          isLoggingOut.current = true;
          auth.signoutRedirect().catch((error) => {
            logger.error("Signout redirect failed:", error);
            isLoggingOut.current = false;
          });
        }
      }, IDLE_TIMEOUT);
    };

    const events = ["mousemove", "keydown", "mousedown", "touchstart"];
    events.forEach((event) => window.addEventListener(event, resetTimer));
    resetTimer();

    return () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      events.forEach((event) => window.removeEventListener(event, resetTimer));
    };
  }, [auth.isAuthenticated, auth.isLoading]);

  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
};

export default App;
